import React, { useMemo, useState } from "react"
import { useRefreshCalendarBookings } from "pagesV2/pro/Calendar/useRefreshCalendarBookings"
import { useSelector } from "react-redux"
import { filter } from "lodash"
import moment from "moment"
import { useNavigate } from "react-router-dom"
import { DATE_FORMAT } from "modules/datepicker/constants"
import { routes } from "router"
import IconWithBg from "ui/IconWithBg"
import { getJournalIcon, overlaps, parseTime, parseTimeOffRanges } from "pagesV2/pro/Calendar/utils"
import styles from "../index.module.css"
import { PlusCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDevice } from "hooks/useDevice"
import { Button, Flex } from "antd"
import { Text } from "ui/Typography"
import TimeOffTooltip from "../TimeOffTooltip"
import { EllipsisIcon } from "icons"
import BookingItem from "../BookingItem"
import { genCalendarStyles } from "antd/es/calendar/style"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import useSearchFilter from "hooks/useSearchFilter"

function renderOverlays(timeOffRanges, allDayTimeOff) {
  return (
    <>
      {!allDayTimeOff && timeOffRanges?.length ? <div aria-hidden className={styles.surfaceOverlay} /> : null}
    </>
  )
}

function renderHeaderIcons({
  isMobile,
  isTablet,
  active,
  journalNoteDates,
  formattedDate,
  handleJournalModalOpen,
  handleAddBooking,
  isPastDay,
  allDayTimeOff,
  canViewJournal,
  canCreateJournal,
  canEditJournal,
  hasJournalNote,
  canCreateBooking
}) {
  if (isMobile || isTablet) return null

  const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal

  return (
    <Flex className={styles.iconHover}>
      {hasJournalPermission && (
        <IconWithBg
          disabled={!active}
          icon={getJournalIcon(journalNoteDates, formattedDate)}
          onClick={active ? () => handleJournalModalOpen(formattedDate) : undefined}
        />
      )}
      {canCreateBooking && !isPastDay && !allDayTimeOff && (
        <IconWithBg
          disabled={!active}
          icon={<PlusCircleOutlined color="var(--primary-color)" />}
          onClick={active ? handleAddBooking : undefined}
          className={styles.addIcon}
        />
      )}
    </Flex>
  )
}

function renderEventsList({ renderList, visibleCount, onShowMore, onShowLess, isMobile, isTablet, t }) {
  if (!renderList?.length) return null
  const total = renderList.length
  const allVisible = visibleCount >= total
  const showLess = allVisible && total > 2

  return (
    <Flex vertical className="z-1 mt-s" gap={2}>
      {renderList.slice(0, visibleCount)}
      {!allVisible && (
        <Button
          className={styles.viewMoreButton}
          type="link"
          onClick={onShowMore}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onShowMore() }}
        >
          {isMobile || isTablet ? <EllipsisIcon height={18} width={18} fill="var(--color-primary)" /> : t("calendar.view_more")}
        </Button>
      )}
      {showLess && (
        <Button
          className={styles.viewMoreButton}
          type="link"
          onClick={onShowLess}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onShowLess() }}
        >
          {t("calendar.view_less", "View less")}
        </Button>
      )}
    </Flex>
  )
}

function renderDayCellContent({
  timeOffRanges,
  allDayTimeOff,
  formattedDate,
  journalNoteDates,
  handleJournalModalOpen,
  handleAddBooking,
  day,
  renderList,
  visibleCount,
  onShowMore,
  onShowLess,
  onChangeFilters,
  isMobile,
  isTablet,
  active,
  isPastDay,
  t,
  canViewJournal,
  canCreateJournal,
  canEditJournal,
  hasJournalNote,
  canCreateBooking
}) {
  return (
    <>
      {!allDayTimeOff && timeOffRanges?.length ? (
        <TimeOffTooltip
          timeOffRanges={timeOffRanges}
          formattedDate={formattedDate}
          journalNoteDates={journalNoteDates}
          handleJournalModalOpen={handleJournalModalOpen}
          handleAddBooking={handleAddBooking}
          day={day}
          renderList={renderList}
          onChangeFilters={onChangeFilters}
        />
      ) : (
        <div className={styles.timeOffContainer}>
          {renderOverlays(timeOffRanges, allDayTimeOff)}
          <Flex justify="center" align="center">
            <Text>{day.format("DD")}</Text>
            {renderHeaderIcons({
              isMobile,
              isTablet,
              active,
              journalNoteDates,
              formattedDate,
              handleJournalModalOpen,
              handleAddBooking,
              isPastDay,
              allDayTimeOff,
              canViewJournal,
              canCreateJournal,
              canEditJournal,
              hasJournalNote,
              canCreateBooking
            })}
          </Flex>
          {renderEventsList({ renderList, visibleCount, onShowMore, onShowLess, isMobile, isTablet, t })}
        </div>
      )}
    </>
  )
}

export const useDayCell = ({ day, timeOffRanges = [], allDayTimeOff = false, onChangeFilters, hideBookings = false }) => {
  const { bookings, journalNoteDates } = useSelector((state) => state.calendar)

  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const [visibleCount, setVisibleCount] = useState(2)
  const onShowMore = () => setVisibleCount((c) => c + 2)
  const onShowLess = () => setVisibleCount(2)

  const navigate = useNavigate()
  const { t } = useTranslation()
  const formattedDate = day.format(DATE_FORMAT)
  const [tabParams] = useSearchFilter({ accessAttributes: ["tab"] })
  const activeTab = tabParams.tab || 'all-bookings'
  const { isMobile, isTablet } = useDevice()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()

  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)
  const hasJournalNote = journalNoteDates?.includes(formattedDate)

  const isPastDay = useMemo(() => day.isBefore(moment().startOf("day"), "day"), [day])
  const parsedTimeOff = useMemo(() => parseTimeOffRanges(timeOffRanges || []), [timeOffRanges])

  const dayBookings = useMemo(() => {
    return filter(bookings, ["date", formattedDate]) || []
  }, [bookings, formattedDate])

  const renderList = useMemo(() => {
    if (hideBookings) return []
    return [...dayBookings]
      .sort((a, b) => {
        const startTimeA = moment(a?.start_time, "HH:mm:ss", true)
        const startTimeB = moment(b?.start_time, "HH:mm:ss", true)
        const isValidA = startTimeA.isValid()
        const isValidB = startTimeB.isValid()
        // If both are valid, compare normally
        if (isValidA && isValidB) {
          const startTimeDiff = startTimeA.diff(startTimeB)
          if (startTimeDiff !== 0) return startTimeDiff
        } else if (isValidA) {
          return -1 // a has a valid time, b does not
        } else if (isValidB) {
          return 1 // b has a valid time, a does not
        } else {
          return 0 // neither has a valid time
        }

        // If start times are equal, compare end times
        const endTimeA = moment(a?.end_time, "HH:mm:ss", true)
        const endTimeB = moment(b?.end_time, "HH:mm:ss", true)
        const isEndValidA = endTimeA.isValid()
        const isEndValidB = endTimeB.isValid()
        if (isEndValidA && isEndValidB) {
          return endTimeA.diff(endTimeB)
        } else if (isEndValidA) {
          return -1
        } else if (isEndValidB) {
          return 1
        } else {
          return 0
        }
      })
      .map((booking) => {
        const bStart = parseTime(booking?.start_time)
        const bEnd = parseTime(booking?.end_time)
        const hasConflict = Boolean(allDayTimeOff) || parsedTimeOff.some((r) => overlaps(bStart, bEnd, r.start, r.end))
        return (
          <BookingItem
            key={booking?.id}
            booking={booking}
            conflict={hasConflict}
            setIsBookingModalOpen={setIsBookingModalOpen}
            setSelectedBookingId={setSelectedBookingId}
            className={genCalendarStyles.eventItem}
            compact={activeTab === 'crew' || activeTab === 'equipment'}
          />
        )
      })
  }, [dayBookings, parsedTimeOff, allDayTimeOff, hideBookings])

  const handleJournalModalOpen = (date) => {
    setIsJournalModalOpen(true)
    setSelectedDate(date)
  }

  const handleCancel = () => {
    setIsJournalModalOpen(false)
  }

  const handleAddBooking = () => {
    if (allDayTimeOff) return
    navigate(routes.guideBookingNewPath())
  }

  const refreshCalendarBookings = useRefreshCalendarBookings()
  const handleBookingCancel = () => {
    setIsBookingModalOpen(false)
    setSelectedBookingId(null)
  }
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  const setJournalOpen = (open) => setIsJournalModalOpen(!!open)

  const content = renderDayCellContent({
    timeOffRanges,
    allDayTimeOff,
    formattedDate,
    journalNoteDates,
    handleJournalModalOpen,
    handleAddBooking,
    day,
    renderList,
    visibleCount,
    onShowMore,
    onShowLess,
    onChangeFilters,
    isMobile,
    isTablet,
    active,
    isPastDay,
    t,
    canViewJournal,
    canCreateJournal,
    canEditJournal,
    hasJournalNote,
    canCreateBooking
  })

  return {
    // state
    isJournalModalOpen,
    isBookingModalOpen,
    selectedDate,
    selectedBookingId,

    // derived
    formattedDate,
    isPastDay,

    // content
    content,

    // handlers
    handleCancel,
    handleBookingCancel,
    handleBookingCancelSuccess,
    refreshCalendarBookings,
    setJournalOpen
  }
}
